import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from 'design-system';

import { DateTextVariants, useDateText } from '@/features/_shared/user';
import copyToClipboard from '@/shared/lib/copyToClipboard';
import { Add as AddIcon, ContentCopyOutlined, VpnKeyOutlined } from '@/shared/ui/icons';
import Button from '@/shared/ui/primitives/Button';

import ConfirmDialog from './ConfirmDialog';
import useApiKeyTable from './useApiKeyTable';

import type { ApiKeyOutput } from '../api';

const ApiKeyTable = ({
  apiKeys,
  onGenerateToken
}: {
  apiKeys: ApiKeyOutput[];
  onGenerateToken: () => void;
}) => {
  const { open, setOpen, openConfirmModal, deleteKey } = useApiKeyTable();
  const { formatDateString } = useDateText();

  const emptyMessage = (
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant='icon'>
          <VpnKeyOutlined />
        </EmptyMedia>
        <EmptyTitle>No token generated yet.</EmptyTitle>
      </EmptyHeader>
      <EmptyContent>
        <Button
          size='small'
          startIcon={<AddIcon />}
          variant='contained'
          onClick={onGenerateToken}
        >
          Generate new token
        </Button>
      </EmptyContent>
    </Empty>
  );

  return (
    <div className='flex w-full items-center justify-between'>
      <ConfirmDialog
        deleteCallback={deleteKey}
        open={open}
        setOpen={setOpen}
      />
      {apiKeys.length ? (
        <Table
          aria-label='api keys table'
          className='min-w-[600px]'
        >
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Access Token</TableHead>
              <TableHead>Creation Date</TableHead>
              <TableHead>Last Used</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...apiKeys]
              .toSorted((a, b) => b.creationDate.localeCompare(a.creationDate))
              .map(apiKey => (
                <TableRow key={apiKey.label}>
                  <TableCell className='w-1/4 max-w-[200px] truncate'>{apiKey.label}</TableCell>
                  <TableCell className='w-1/4'>
                    {apiKey.prefixedRawApiKey && (
                      <div className='flex max-w-[210px] items-center overflow-x-auto overflow-y-hidden'>
                        <span className='mr-1'>{`${apiKey.prefixedRawApiKey.substring(0, 9)}**********`}</span>
                        <Button
                          size='small'
                          startIcon={<ContentCopyOutlined />}
                          variant='text'
                          onClick={() =>
                            copyToClipboard(
                              apiKey.prefixedRawApiKey ?? '',
                              'Api key copied to clipboard!'
                            )
                          }
                        >
                          Copy
                        </Button>
                      </div>
                    )}
                    {!apiKey.prefixedRawApiKey && (
                      <span className='max-w-[200px] overflow-x-auto overflow-y-hidden'>
                        {`**********${apiKey.lastThreeCharacters}`}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className='w-[15%]'>
                    {formatDateString(apiKey.creationDate, DateTextVariants.FULL)}
                  </TableCell>
                  <TableCell className='w-[30%]'>
                    {formatDateString(apiKey.lastUsed ?? '---', DateTextVariants.WITH_TIME)}
                  </TableCell>
                  <TableCell className='w-[15%]'>
                    <Button
                      color='secondary'
                      size='small'
                      variant='text'
                      onClick={() => {
                        openConfirmModal(apiKey);
                      }}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      ) : (
        emptyMessage
      )}
    </div>
  );
};

export default ApiKeyTable;
